import React, { Suspense, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import bem from 'utils/bem';
import Text from 'components/Text';
import Page from '../Page';
import CardTeaser, { CardTeaserSkeleton } from './Teaser';
import { faq } from './data';
import { times } from 'utils/render';
import Model from '../../../model';
import './index.sass';
import { useMobile } from 'utils/media';
import MobileCardTeaser, { MobileCardTeaserSkeleton } from './MobileTeaser';

const { block, el } = bem('foreign-payment');

const colors = [
    '#EBD9CF',
    '#EDEBCF',
    '#D2D9D8',
    '#E5DCEA',
    '#F4E4E4',
    '#CFE1E8',
]

const ForeignPayments = () => {
    const { cardsData } = Model.cards;
    const mailInfo = `По всем вопросам мы всегда рады ответить Вам по почте <a href='mailto:hello@otvet.co'}>hello@otvet.co</a>`;
    const faqRef = useRef(null);
    const navigate = useNavigate();

    const { openLoginForm } = Model.modal;
    const { isAuthenticated } = Model.user;

    useEffect(() => {
        if (faqRef.current) {
            const button = faqRef.current.querySelector('#register');
            if (button) {
                button.onclick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isAuthenticated) {
                        navigate('/profile')
                    } else {
                        openLoginForm({ initial: 'register' });
                    }
                }
            }
        }
    }, [ faqRef.current ])

    return (
        <Page className={ block() } title={ 'Оплата иностранными банковскими картами' }>
            <div className={ el('top') }>
                <div className={ el('top-content') }>
                    <div className={ el('top-text') }>
                        <Text>
                            Для клиентов, которые приобретают сессии у специалистов или иные продукты Otvet.co с помощью
                            инностранных банковских карт, мы предоставляем новую систему оплаты. Этот способ позволяет
                            беспрепятственно производить платежи с помощью карт, выпущенных за пределами России.
                        </Text>
                        <Text>
                            Перед вами карты Otvet.co с разными номиналами - от 1500 до 20000 тысяч рублей. Выбирайте
                            необходимую сумму, оплачиваете с помощью вашей банковской карты. После оплаты сумма будет
                            отображена на балансе в вашем личном кабинете. Приобретайте продукты Otvet.co, используя
                            баланс аккаунта.
                        </Text>
                    </div>
                    <div className={ el('top-steps') }>
                        <div className={ el('top-steps-title') }>
                            <Text serif>Шаги</Text>
                        </div>
                        <div className={ el('top-steps-list') }>
                            <div className={ el('top-step') }>
                                <div className={ el('top-step-number') }>
                                    <Text>1</Text>
                                </div>
                                <div className={ el('top-step-value') }>
                                    <Text>Приобрести карту нужного Вам номинала</Text>
                                </div>
                            </div>
                            <div className={ el('top-step') }>
                                <div className={ el('top-step-number') }>
                                    <Text>2</Text>
                                </div>
                                <div className={ el('top-step-value') }>
                                    <Text>Выбрать сессию у специалиста или продукт в интернет-магазине</Text>
                                </div>
                            </div>
                            <div className={ el('top-step') }>
                                <div className={ el('top-step-number') }>
                                    <Text>3</Text>
                                </div>
                                <div className={ el('top-step-value') }>
                                    <Text>На этапе оплаты выбранного продукта оплатить его со счета своего
                                        аккаунта</Text>
                                </div>
                            </div>
                            <div className={ el('top-step') }>
                                <div className={ el('top-step-number') }>
                                    <Text>4</Text>
                                </div>
                                <div className={ el('top-step-value') }>
                                    <Text>После оплаты приобретенная покупка отобразится в Вашем личном кабинете</Text>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={ el('top-image') }>
                    <div className={ el('pic') }/>
                </div>
            </div>
            <div className={ el('cards') }>
                <Suspense fallback={ <CardsBlockSkeleton/> }>
                    <CardsBlock resource={ cardsData }/>
                </Suspense>
            </div>
            <div className={ el('info') }>
                <Text>
                    *Пожалуйста, обратите внимание, что стоимость карточек списывается в
                    иностранной валюте (казахстанский тенге)
                </Text>
            </div>
            <div className={ el('faq') }>
                <div className={ el('faq-title') }>
                    <Text serif>FAQ</Text>
                </div>
                <div className={ el('faq-list') } ref={ faqRef }>
                    { faq.map((answer, index) =>
                        <div key={ index } className={ el('faq-answer') }>
                            <Text dangerouslySetInnerHTML={ { __html: answer } }/>
                        </div>) }
                    <div className={ el('faq-mail-info') }>
                        <Text dangerouslySetInnerHTML={ { __html: mailInfo } }/>
                    </div>
                </div>
            </div>
        </Page>
    )
}

const CardsBlock = ({ resource }) => {
    const cards = resource.read();
    const { isMobile } = useMobile();
    const Teaser = isMobile ? MobileCardTeaser : CardTeaser;
    return (
        <>
            { cards.map((card, i) => <Teaser key={ card.id } card={ card } color={ colors[i % 6] }/>) }
        </>
    )
}

const CardsBlockSkeleton = () => {
    const { isMobile } = useMobile();
    const TeaserSkeleton = isMobile ? MobileCardTeaserSkeleton : CardTeaserSkeleton;
    return (
        <>
            { times((key) => <TeaserSkeleton key={ key }/>, 6) }
        </>
    )
}

export default observer(ForeignPayments);
